import React, { useEffect, useRef, useState } from 'react';

import _ from 'lodash';
import { Checkbox, Chip, CircularProgress, IconButton, Select } from '@material-ui/core';
import { InputAdornment, ListItemText, MenuItem, TextField } from '@material-ui/core';
import { MdFilterList, MdSearch, MdClear } from 'react-icons/md';

import { useValue } from 'hooks';
import { call } from 'utils';

import styles from './SearchField.module.scss';

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 54 * 8 + 8,
    },
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
};

const SelectInput = props => {
  const { value, setValue, options, multi, setActive, optionsParams } = props;

  const [data, setData] = useState();
  const searchParams = useValue(optionsParams);

  useEffect(() => {
    const setup = data => {
      setData(data);

      setValue(current =>
        current && Array.isArray(data) && !data.find(it => _.isEqual(it.value, current))
          ? ''
          : current
      );
    };

    if (Array.isArray(options)) {
      setup(options);
    } else if (typeof options === 'function') {
      const data = options(searchParams);

      if (data instanceof Promise) {
        setup(null);
        data.then(val => setup(Array.isArray(val) ? val : []));
      } else {
        setup(data);
      }
    } else {
      setup([]);
    }
  }, [options, searchParams, setValue]);

  if (Array.isArray(data)) {
    if (multi) {
      const values = (value || '')
        .split(',')
        .map(it => (it || '').trim())
        .filter(it => !!it);

      return (
        <Select
          multiple
          value={values}
          onChange={event => setValue((event.target.value || []).join(','))}
          renderValue={selected => (
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {selected.map(value => (
                <Chip
                  key={value}
                  label={(data.find(it => it.value === value) || {}).text}
                  size="small"
                />
              ))}
            </div>
          )}
          fullWidth
          onOpen={() => setActive(false)}
          onClose={() => setActive(true)}
          MenuProps={MenuProps}
        >
          {data.map(({ text, value }, index) => (
            <MenuItem key={`${value}_${index}`} value={value}>
              <Checkbox
                checked={values.indexOf(value) > -1}
                value={value}
                color="primary"
                size="small"
              />
              <ListItemText primary={text} />
            </MenuItem>
          ))}
        </Select>
      );
    }

    return (
      <Select value={value} onChange={event => setValue(event.target.value)} fullWidth>
        <MenuItem value="">
          <em>Any</em>
        </MenuItem>

        {data.map(({ text, value }, index) => (
          <MenuItem key={`${value}_${index}`} value={value}>
            {text}
          </MenuItem>
        ))}
      </Select>
    );
  }

  return (
    <div className={styles.loader}>
      <CircularProgress size={20} />
    </div>
  );
};

const SearchField = props => {
  const { filter, type = 'text', options, onChange, optionsParams } = props;

  const [value, setValue] = useState(props.value || '');
  const [active, setActive] = useState(true);
  const lastValue = useRef(value);

  useEffect(() => {
    if (active && value !== lastValue.current) {
      lastValue.current = value;
      const timer = setTimeout(() => call(onChange, value), 500);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [value, active, onChange]);

  if (type === 'text') {
    return (
      <TextField
        value={value}
        onChange={event => setValue(event.target.value)}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {filter ? (
                <MdFilterList style={{ fontSize: 20 }} />
              ) : (
                <MdSearch style={{ fontSize: 20 }} />
              )}
            </InputAdornment>
          ),
          endAdornment: filter ? undefined : (
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  if (value) {
                    setValue((lastValue.current = ''));
                    call(onChange, '');
                  }
                }}
              >
                <MdClear style={{ fontSize: 20 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  }

  if (type === 'select') {
    return (
      <SelectInput
        value={value}
        setValue={setValue}
        options={options}
        optionsParams={optionsParams}
      />
    );
  }

  if (type === 'multi') {
    return (
      <SelectInput
        multi
        value={value}
        setValue={setValue}
        options={options}
        setActive={setActive}
        optionsParams={optionsParams}
      />
    );
  }

  return null;
};

export default SearchField;
